import { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import DashboardLayout from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Plus, Edit, Trash2, Users, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ClassesPage = () => {
  const { getAuthHeader } = useAuth();
  const [classes, setClasses] = useState([]);
  const [sections, setSections] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [students, setStudents] = useState([]);
  
  // Class dialog states
  const [classDialogOpen, setClassDialogOpen] = useState(false);
  const [editingClass, setEditingClass] = useState(null);
  const [classFormData, setClassFormData] = useState({ name: '', description: '' });
  
  // Section dialog states
  const [sectionDialogOpen, setSectionDialogOpen] = useState(false);
  const [editingSection, setEditingSection] = useState(null);
  const [sectionFormData, setSectionFormData] = useState({ class_id: '', name: '', teacher_id: '' });
  
  // Delete confirmation states
  const [deleteClassDialog, setDeleteClassDialog] = useState(false);
  const [deleteSectionDialog, setDeleteSectionDialog] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [deleteError, setDeleteError] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [classesRes, sectionsRes, teachersRes, studentsRes] = await Promise.all([
        axios.get(`${API_URL}/api/classes`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/api/sections`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/api/teachers`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/api/students`, { headers: getAuthHeader() })
      ]);
      setClasses(classesRes.data);
      setSections(sectionsRes.data);
      setTeachers(teachersRes.data.items || teachersRes.data || []);
      setStudents(studentsRes.data.items || studentsRes.data || []);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load data');
    }
  };

  // Get student count for a class (use from API if available, fallback to local filter)
  const getClassStudentCount = (classId) => {
    const cls = classes.find(c => c.id === classId);
    if (cls && cls.student_count !== undefined) {
      return cls.student_count;
    }
    return students.filter(s => s.class_id === classId).length;
  };

  // Get student count for a section (use from API if available, fallback to local filter)
  const getSectionStudentCount = (sectionId) => {
    const section = sections.find(s => s.id === sectionId);
    if (section && section.student_count !== undefined) {
      return section.student_count;
    }
    return students.filter(s => s.section_id === sectionId).length;
  };

  // Class handlers
  const openClassDialog = (cls = null) => {
    if (cls) {
      setEditingClass(cls);
      setClassFormData({ name: cls.name, description: cls.description || '' });
    } else {
      setEditingClass(null);
      setClassFormData({ name: '', description: '' });
    }
    setClassDialogOpen(true);
  };

  const handleClassSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingClass) {
        await axios.put(`${API_URL}/api/classes/${editingClass.id}`, classFormData, {
          headers: getAuthHeader()
        });
        toast.success('Class updated successfully');
      } else {
        await axios.post(`${API_URL}/api/classes`, classFormData, {
          headers: getAuthHeader()
        });
        toast.success('Class added successfully');
      }
      setClassDialogOpen(false);
      setClassFormData({ name: '', description: '' });
      setEditingClass(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save class');
    }
  };

  const confirmDeleteClass = (cls) => {
    const studentCount = getClassStudentCount(cls.id);
    if (studentCount > 0) {
      setDeleteError(`This class has ${studentCount} student(s). Please transfer or remove students first.`);
    } else {
      setDeleteError('');
    }
    setItemToDelete(cls);
    setDeleteClassDialog(true);
  };

  const handleDeleteClass = async () => {
    if (!itemToDelete) return;
    
    const studentCount = getClassStudentCount(itemToDelete.id);
    if (studentCount > 0) {
      toast.error(`Cannot delete: ${studentCount} student(s) in this class`);
      return;
    }

    try {
      await axios.delete(`${API_URL}/api/classes/${itemToDelete.id}`, {
        headers: getAuthHeader()
      });
      toast.success('Class deleted successfully');
      setDeleteClassDialog(false);
      setItemToDelete(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete class');
    }
  };

  // Section handlers
  const openSectionDialog = (section = null) => {
    if (section) {
      setEditingSection(section);
      setSectionFormData({ 
        class_id: section.class_id, 
        name: section.name, 
        teacher_id: section.teacher_id || '' 
      });
    } else {
      setEditingSection(null);
      setSectionFormData({ class_id: '', name: '', teacher_id: '' });
    }
    setSectionDialogOpen(true);
  };

  const handleSectionSubmit = async (e) => {
    e.preventDefault();
    
    // Validate Class Teacher is selected
    if (!sectionFormData.teacher_id) {
      toast.error('Class Teacher is required');
      return;
    }

    try {
      if (editingSection) {
        await axios.put(`${API_URL}/api/sections/${editingSection.id}`, sectionFormData, {
          headers: getAuthHeader()
        });
        toast.success('Section updated successfully');
      } else {
        await axios.post(`${API_URL}/api/sections`, sectionFormData, {
          headers: getAuthHeader()
        });
        toast.success('Section added successfully');
      }
      setSectionDialogOpen(false);
      setSectionFormData({ class_id: '', name: '', teacher_id: '' });
      setEditingSection(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save section');
    }
  };

  const confirmDeleteSection = (section) => {
    const studentCount = getSectionStudentCount(section.id);
    if (studentCount > 0) {
      setDeleteError(`This section has ${studentCount} student(s). Please transfer or remove students first.`);
    } else {
      setDeleteError('');
    }
    setItemToDelete(section);
    setDeleteSectionDialog(true);
  };

  const handleDeleteSection = async () => {
    if (!itemToDelete) return;
    
    const studentCount = getSectionStudentCount(itemToDelete.id);
    if (studentCount > 0) {
      toast.error(`Cannot delete: ${studentCount} student(s) in this section`);
      return;
    }

    try {
      await axios.delete(`${API_URL}/api/sections/${itemToDelete.id}`, {
        headers: getAuthHeader()
      });
      toast.success('Section deleted successfully');
      setDeleteSectionDialog(false);
      setItemToDelete(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete section');
    }
  };

  return (
    <DashboardLayout title="Classes & Sections">
      <div className="space-y-6">
        {/* Classes Section */}
        <Card data-testid="classes-card">
          <CardHeader className="flex flex-row items-center justify-between space-y-0">
            <CardTitle>Classes ({classes.length})</CardTitle>
            <Button size="sm" data-testid="add-class-btn" onClick={() => openClassDialog()}>
              <Plus className="h-4 w-4 mr-2" />
              Add Class
            </Button>
          </CardHeader>
          <CardContent>
            {classes.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">No classes found</p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {classes.map(cls => {
                  const studentCount = getClassStudentCount(cls.id);
                  return (
                    <div key={cls.id} data-testid={`class-item-${cls.id}`} className="p-4 border border-border rounded-lg hover:bg-muted/50 transition-colors">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <h3 className="font-semibold text-lg">{cls.name}</h3>
                          <p className="text-sm text-muted-foreground mt-1">{cls.description || 'No description'}</p>
                          <div className="flex items-center gap-1 mt-2 text-sm text-muted-foreground">
                            <Users className="h-4 w-4" />
                            <span>{studentCount} student(s)</span>
                          </div>
                        </div>
                        <div className="flex gap-1">
                          <Button variant="ghost" size="icon" onClick={() => openClassDialog(cls)}>
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => confirmDeleteClass(cls)} className="text-destructive hover:text-destructive">
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Sections Section */}
        <Card data-testid="sections-card">
          <CardHeader className="flex flex-row items-center justify-between space-y-0">
            <CardTitle>Sections ({sections.length})</CardTitle>
            <Button size="sm" data-testid="add-section-btn" disabled={classes.length === 0} onClick={() => openSectionDialog()}>
              <Plus className="h-4 w-4 mr-2" />
              Add Section
            </Button>
          </CardHeader>
          <CardContent>
            {sections.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">No sections found</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-3 px-4 font-medium">Section</th>
                      <th className="text-left py-3 px-4 font-medium">Class</th>
                      <th className="text-left py-3 px-4 font-medium">Class Teacher</th>
                      <th className="text-left py-3 px-4 font-medium">Students</th>
                      <th className="text-left py-3 px-4 font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sections.map(section => {
                      const sectionClass = classes.find(c => c.id === section.class_id);
                      const teacher = teachers.find(t => t.id === section.teacher_id);
                      const studentCount = getSectionStudentCount(section.id);
                      return (
                        <tr key={section.id} data-testid={`section-row-${section.id}`} className="border-b hover:bg-muted/50 transition-colors">
                          <td className="py-3 px-4 font-medium">{section.name}</td>
                          <td className="py-3 px-4">{sectionClass?.name || '-'}</td>
                          <td className="py-3 px-4">{teacher?.name || <span className="text-destructive">Not assigned</span>}</td>
                          <td className="py-3 px-4">
                            <div className="flex items-center gap-1">
                              <Users className="h-4 w-4 text-muted-foreground" />
                              <span>{studentCount}</span>
                            </div>
                          </td>
                          <td className="py-3 px-4">
                            <div className="flex gap-1">
                              <Button variant="ghost" size="icon" onClick={() => openSectionDialog(section)}>
                                <Edit className="h-4 w-4" />
                              </Button>
                              <Button variant="ghost" size="icon" onClick={() => confirmDeleteSection(section)} className="text-destructive hover:text-destructive">
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Class Add/Edit Dialog */}
      <Dialog open={classDialogOpen} onOpenChange={setClassDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingClass ? 'Edit Class' : 'Add New Class'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleClassSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="class-name">Class Name *</Label>
              <Input
                id="class-name"
                value={classFormData.name}
                onChange={(e) => setClassFormData({ ...classFormData, name: e.target.value })}
                data-testid="class-name-input"
                placeholder="e.g., Class 10"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="class-description">Description</Label>
              <Input
                id="class-description"
                value={classFormData.description}
                onChange={(e) => setClassFormData({ ...classFormData, description: e.target.value })}
                data-testid="class-description-input"
                placeholder="Optional description"
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setClassDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" data-testid="submit-class-btn">
                {editingClass ? 'Update Class' : 'Add Class'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Section Add/Edit Dialog */}
      <Dialog open={sectionDialogOpen} onOpenChange={setSectionDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingSection ? 'Edit Section' : 'Add New Section'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSectionSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="section-class">Class *</Label>
              <select
                id="section-class"
                className="w-full h-10 rounded-md border border-input bg-background px-3 py-2"
                value={sectionFormData.class_id}
                onChange={(e) => setSectionFormData({ ...sectionFormData, class_id: e.target.value })}
                required
              >
                <option value="">Select class</option>
                {classes.map(cls => (
                  <option key={cls.id} value={cls.id}>{cls.name}</option>
                ))}
              </select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="section-name">Section Name *</Label>
              <Input
                id="section-name"
                value={sectionFormData.name}
                onChange={(e) => setSectionFormData({ ...sectionFormData, name: e.target.value })}
                data-testid="section-name-input"
                placeholder="e.g., Section A"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="section-teacher">Class Teacher *</Label>
              <select
                id="section-teacher"
                className="w-full h-10 rounded-md border border-input bg-background px-3 py-2"
                value={sectionFormData.teacher_id}
                onChange={(e) => setSectionFormData({ ...sectionFormData, teacher_id: e.target.value })}
                required
              >
                <option value="">Select teacher (required)</option>
                {teachers.filter(t => t.status === 'active' || !t.status).map(teacher => (
                  <option key={teacher.id} value={teacher.id}>{teacher.name}</option>
                ))}
              </select>
              <p className="text-xs text-muted-foreground">Class teacher assignment is mandatory</p>
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setSectionDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" data-testid="submit-section-btn">
                {editingSection ? 'Update Section' : 'Add Section'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Class Confirmation Dialog */}
      <Dialog open={deleteClassDialog} onOpenChange={setDeleteClassDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Class</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{itemToDelete?.name}"?
            </DialogDescription>
          </DialogHeader>
          {deleteError && (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>{deleteError}</AlertDescription>
            </Alert>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteClassDialog(false)}>Cancel</Button>
            <Button 
              variant="destructive" 
              onClick={handleDeleteClass}
              disabled={!!deleteError}
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Section Confirmation Dialog */}
      <Dialog open={deleteSectionDialog} onOpenChange={setDeleteSectionDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Section</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{itemToDelete?.name}"?
            </DialogDescription>
          </DialogHeader>
          {deleteError && (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>{deleteError}</AlertDescription>
            </Alert>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteSectionDialog(false)}>Cancel</Button>
            <Button 
              variant="destructive" 
              onClick={handleDeleteSection}
              disabled={!!deleteError}
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};

export default ClassesPage;
